import { assertNever } from "../index.ts";

export type SsoProvider =
  | AzureOIDCProvider
  | GoogleOIDCProvider
  | GoogleSsoProvider
  | LegacyOktaSsoProvider
  | MicrosoftSsoProvider
  | OidcPkceProvider;

export type GoogleSsoProvider = {
  ssoProvider: "google";
};

/** Legacy Okta provider
 *
 * @deprecated Use OidcPkceProvider instead
 *
 * This is kept for customers that do not yet enforce
 * PKCE.
 */
export type LegacyOktaSsoProvider = {
  ssoProvider: "okta";
  providerId: string;
};

export type GoogleOIDCProvider = {
  ssoProvider: "google-oidc";
};

type CommonOidcProvider = {
  providerId: string;
  providerDomain: string;
  clientId: string;
};

export type MicrosoftSsoProvider = Partial<CommonOidcProvider> & {
  ssoProvider: "microsoft";
  microsoftPrimaryDomain: string;
};

export type AzureOIDCProvider = Partial<CommonOidcProvider> & {
  ssoProvider: "azure-oidc";
  microsoftPrimaryDomain: string;
};

type BaseOidcPkceProvider = CommonOidcProvider & {
  ssoProvider: "oidc-pkce";
};

export type OktaOidcPkceProvider = BaseOidcPkceProvider & {
  providerType: "okta";
  /** Defines the specific authentication server to use.
   *
   * If present, server is located at
   *
   *   `https://{providerDomain}/oauth2/{authServerPath}/v1`;
   *
   * otherwise, it is at
   *
   *   `https://{providerDomain}/oauth2/v1`.
   */
  authServerPath?: string;
};

type PingIdOidcPkceProvider = BaseOidcPkceProvider & {
  providerType: "ping";
  environmentId: string;
};

type CloudflareOidcPkceProvider = BaseOidcPkceProvider & {
  providerType: "cloudflare";
  clientSecret: string;
};

export type OidcPkceProvider =
  | CloudflareOidcPkceProvider
  | OktaOidcPkceProvider
  | PingIdOidcPkceProvider;

/** Base URL of the organization's OIDC authorization server.
 *
 * The browser's `/authorize` request and P0's server-side `/token` exchange must
 * both target this same base. An authorization code is only redeemable at the
 * authorization server that issued it, so deriving the two endpoints separately
 * lets them drift — which is how Okta orgs on a custom `authServerPath` ended up
 * redeeming codes at the org authorization server and getting back
 * `invalid_client` (ENG-8521).
 */
export const oidcAuthServerUrl = (provider: OidcPkceProvider): string => {
  switch (provider.providerType) {
    case "okta": {
      const asPath = provider.authServerPath
        ? `${provider.authServerPath}/v1`
        : "v1";
      return `https://${provider.providerDomain}/oauth2/${asPath}`;
    }
    case "ping":
      return `https://${provider.providerDomain}/${provider.environmentId}/as`;
    case "cloudflare":
      return `https://${provider.providerDomain}/cdn-cgi/access/sso/oidc/${provider.clientId}`;
    default:
      return assertNever(provider);
  }
};
